import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

const UpdatePassword: React.FC = () => {
  const { user, updatePassword } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      return setError('Please fill in all fields');
    }

    if (newPassword !== confirmPassword) {
      return setError('New passwords do not match');
    }

    if (newPassword.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    if (currentPassword === newPassword) {
      return setError('New password must be different from current password');
    }

    try {
      setError('');
      setLoading(true);

      // First, reauthenticate the user with their current password
      if (!user) throw new Error('No user signed in');
      const credential = EmailAuthProvider.credential(
        user.email!,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      // Then update to the new password
      await updatePassword(newPassword);
      navigate('/profile');
    } catch (err) {
      console.error('Failed to update password:', err);
      if (err instanceof Error && 'code' in err) {
        if (err.code === 'auth/wrong-password') {
          setError('Current password is incorrect. Please try again.');
        } else if (err.code === 'auth/requires-recent-login') {
          setError(
            'This operation requires recent authentication. Please log out and log back in before retrying.'
          );
        } else {
          setError('Failed to update password. Please try again.');
        }
      } else {
        setError('Failed to update password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = {
    maxWidth: '400px',
    margin: '40px auto',
    padding: theme.spacing.LARGE,
    backgroundColor: theme.colors.SURFACE,
    borderRadius: '8px',
    border: `1px solid ${theme.colors.BORDER}`,
  };

  const inputStyle = {
    width: '100%',
    padding: theme.spacing.SMALL,
    marginBottom: theme.spacing.MEDIUM,
    border: `1px solid ${theme.colors.INPUT_BORDER}`,
    borderRadius: '4px',
    fontSize: theme.fonts.MEDIUM,
    backgroundColor: theme.colors.INPUT_BACKGROUND,
    color: theme.colors.INPUT_TEXT,
  };

  const buttonStyle = {
    width: '100%',
    padding: theme.spacing.MEDIUM,
    backgroundColor: theme.colors.BUTTON_PRIMARY_BG,
    color: theme.colors.BUTTON_PRIMARY_TEXT,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.colors.SECONDARY,
    marginTop: theme.spacing.SMALL,
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          input::placeholder {
            color: rgba(0, 0, 0, 0.6) !important;
            opacity: 1;
          }
        `}
      </style>
      <h2
        style={{
          color: theme.colors.TEXT,
          marginBottom: theme.spacing.LARGE,
          textAlign: 'center',
        }}
      >
        Update Password
      </h2>

      {error && (
        <div
          style={{
            backgroundColor: theme.colors.ERROR,
            color: theme.colors.WHITE,
            padding: theme.spacing.MEDIUM,
            borderRadius: '4px',
            marginBottom: theme.spacing.MEDIUM,
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <input
          type="password"
          placeholder="Current Password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          style={inputStyle}
          required
        />
        <input
          type="password"
          placeholder="New Password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          style={inputStyle}
          required
        />
        <input
          type="password"
          placeholder="Confirm New Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          style={inputStyle}
          required
        />
        <button type="submit" disabled={loading} style={buttonStyle}>
          {loading ? 'Updating...' : 'Update Password'}
        </button>
        <button
          type="button"
          onClick={() => navigate('/profile')}
          style={cancelButtonStyle}
        >
          Cancel
        </button>
      </form>
    </div>
  );
};

export default UpdatePassword;
